import React, { createContext, useContext, useMemo } from "react";
import en from "./locales/en.json";
import ptBR from "./locales/pt-BR.json";
import es from "./locales/es.json";
import fr from "./locales/fr.json";
import de from "./locales/de.json";
import it from "./locales/it.json";
import zh from "./locales/zh.json";
import ja from "./locales/ja.json";
import { SupportedLocale } from "./locale";

export type TranslationKey = keyof typeof en;

const dictionaries: Record<SupportedLocale, Record<string, string>> = {
  en,
  "pt-BR": ptBR,
  es,
  fr,
  de,
  it,
  zh,
  ja,
};

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (match, name) =>
    name in vars ? String(vars[name]) : match
  );
}

export type TranslateFn = (key: TranslationKey, vars?: Record<string, string | number>) => string;

type LocaleContextValue = {
  locale: SupportedLocale;
  t: TranslateFn;
};

export function translate(
  locale: SupportedLocale,
  key: TranslationKey,
  vars?: Record<string, string | number>
): string {
  const dict = dictionaries[locale];
  return interpolate(dict[key] ?? dictionaries.en[key] ?? key, vars);
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({
  locale,
  children,
}: {
  locale: SupportedLocale;
  children: React.ReactNode;
}) {
  const value = useMemo<LocaleContextValue>(() => {
    const t: TranslateFn = (key, vars) => translate(locale, key, vars);
    return { locale, t };
  }, [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useTranslation(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useTranslation must be used within a LocaleProvider");
  }
  return context;
}

// Fechado em chaves de tradução de propósito: não existe variante que carregue texto livre.
// Mensagens vindas da rede (corpo de resposta, erro do fetch) podem conter a URL do PABX
// com ?token=<pabxApiToken>, e já vazaram para a tela do usuário por esse caminho. Manter o
// tipo assim faz o compilador barrar qualquer reincidência.
export type LocalizedMessage = { key: TranslationKey; params?: Record<string, string | number> };

export function renderMessage(t: TranslateFn, message: LocalizedMessage): string {
  return t(message.key, message.params);
}
